import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import OperationalTable from '../../../../organisms/OperationalTable'
import SydButton from '../../../../commonDesign/Button'
import EmptySection from '../../../../atoms/EmptySection'
import VersionDiffModal from './VersionDiffModal'

const useStyles = makeStyles(() => ({
  activeBadge: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--color-success-700)',
    backgroundColor: 'var(--color-success-050)',
    padding: '2px 10px',
    borderRadius: '10px'
  },
  actionsCell: {
    display: 'flex',
    gap: '6px'
  }
}))

const CLOSED_DIFF = { open: false, leftId: null, rightId: null }

function VersionHistoryTab ({ template, versions, onSetActive }) {
  const classes = useStyles()
  const [diffState, setDiffState] = useState(CLOSED_DIFF)
  const activeVersionId = template?.definitionVersionId

  const handleCompare = useCallback((versionId) => {
    setDiffState({ open: true, leftId: activeVersionId, rightId: versionId })
  }, [activeVersionId])

  const closeDiff = useCallback(() => setDiffState(CLOSED_DIFF), [])

  /* eslint-disable react/prop-types -- Cell render props from react-table */
  const columns = useMemo(() => [
    {
      id: 'definitionVersionId',
      accessor: 'definitionVersionId',
      Header: 'Version ID'
    },
    {
      id: 'description',
      accessor: 'description',
      Header: 'Description',
      Cell: ({ value }) => value || '\u2014'
    },
    {
      id: 'versionDate',
      accessor: 'versionDate',
      Header: 'Date',
      Cell: ({ value }) => value ? new Date(value).toLocaleString() : '\u2014'
    },
    {
      id: 'status',
      Header: 'Status',
      Cell: ({ row }) => row.original.definitionVersionId === activeVersionId ? (
        <span className={classes.activeBadge}>Active</span>
      ) : null
    },
    {
      id: 'actions',
      Header: '',
      disableSortBy: true,
      Cell: ({ row }) => {
        const version = row.original
        const active = version.definitionVersionId === activeVersionId
        if (active) return null
        return (
          <div className={classes.actionsCell} onClick={(e) => e.stopPropagation()} role='presentation'>
            {activeVersionId && (
              <SydButton variant='ghost' size='sm' onClick={() => handleCompare(version.definitionVersionId)}>
                Compare
              </SydButton>
            )}
            <SydButton variant='outline' size='sm' onClick={() => onSetActive(version)}>
              Set Active
            </SydButton>
          </div>
        )
      }
    }
  ], [activeVersionId, classes, handleCompare, onSetActive])
  /* eslint-enable react/prop-types */

  if (!versions || versions.length === 0) {
    return (
      <EmptySection
        title='No Versions'
        description='Save a definition to create the first version.'
      />
    )
  }

  return (
    <>
      <OperationalTable
        variant='v2'
        columns={columns}
        data={versions}
        defaultSort={[{ id: 'versionDate', desc: true }]}
      />
      <VersionDiffModal
        open={diffState.open}
        onClose={closeDiff}
        actionTemplateId={template?.actionTemplateId}
        leftVersionId={diffState.leftId}
        rightVersionId={diffState.rightId}
      />
    </>
  )
}

VersionHistoryTab.propTypes = {
  template: PropTypes.object.isRequired,
  versions: PropTypes.array.isRequired,
  onSetActive: PropTypes.func.isRequired
}

export default VersionHistoryTab
